import { TFunction } from 'i18next';

const FAN_COURIER_REJECTED = 'FAN_COURIER_REJECTED';

export interface CourierRejectionDetail {
  field?: string;
  message: string;
}

export interface CourierRejection {
  message?: string;
  details: CourierRejectionDetail[];
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;

const asText = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

export const courierRejectionOf = (error: unknown): CourierRejection | undefined => {
  const data = asRecord(asRecord(asRecord(error)?.response)?.data);
  if (data?.code !== FAN_COURIER_REJECTED) return undefined;
  const subErrors = Array.isArray(data.subErrors) ? data.subErrors : [];
  return {
    message: asText(data.message),
    details: subErrors.flatMap((entry): CourierRejectionDetail[] => {
      const record = asRecord(entry);
      const message = asText(record?.message);
      return message ? [{ field: asText(record?.details), message }] : [];
    }),
  };
};

const KNOWN_ERRORS: Record<string, string> = {
  'info.options.incompatibleOptions': 'awb.fan_error.incompatible_options',
  'sender.address.dropOffLocation': 'awb.fan_error.drop_off_location',
};

const FIELD_LABELS: Record<string, string> = {
  'recipient.name': 'awb.fan_field.recipient_name',
  'recipient.contactPerson': 'awb.fan_field.contact_person',
  'recipient.phone': 'awb.fan_field.phone',
  'recipient.secondaryPhone': 'awb.fan_field.phone',
  'recipient.email': 'awb.fan_field.email',
  'recipient.address.county': 'awb.fan_field.county',
  'recipient.address.locality': 'awb.fan_field.locality',
  'recipient.address.street': 'awb.fan_field.street',
  'recipient.address.streetNo': 'awb.fan_field.street_no',
  'recipient.address.zipCode': 'awb.fan_field.zip_code',
  'info.service': 'awb.fan_field.service',
  'info.weight': 'awb.fan_field.weight',
  'info.packages': 'awb.fan_field.packages',
  'info.dimensions': 'awb.fan_field.dimensions',
  'info.declaredValue': 'awb.fan_field.declared_value',
  'info.content': 'awb.fan_field.content',
  'info.observation': 'awb.fan_field.observation',
  'info.options': 'awb.fan_field.options',
};

const fieldLabelKey = (field: string): string | undefined => {
  const match = Object.keys(FIELD_LABELS)
    .filter((path) => field === path || field.startsWith(`${path}.`))
    .sort((a, b) => b.length - a.length)[0];
  return match ? FIELD_LABELS[match] : undefined;
};

const translateDetail = (detail: CourierRejectionDetail, t: TFunction): string => {
  const known = detail.field ? KNOWN_ERRORS[detail.field] : undefined;
  if (known) return t(known);
  const labelKey = detail.field ? fieldLabelKey(detail.field) : undefined;
  if (!labelKey) return t('awb.fan_error.unknown');
  const field = t(labelKey);
  const problem = detail.message.trim().toLowerCase();
  if (problem.endsWith('required')) return t('awb.fan_error.field_required', { field });
  return t('awb.fan_error.field_invalid', { field });
};

export const translateCourierRejection = (rejection: CourierRejection, t: TFunction): string => {
  const details = [...new Set(rejection.details.map((detail) => translateDetail(detail, t)))];
  return details.length > 0
    ? t('awb.fan_error.with_details', { details: details.join(' ') })
    : t('awb.fan_error.title');
};
